export const HOVER_ATTRIBUTE = 'data-cobrowse-hover';
export const FOCUS_ATTRIBUTE = 'data-cobrowse-focus';
export const FOCUS_WITHIN_ATTRIBUTE = 'data-cobrowse-focus-within';

const EMULATED_PSEUDO_CLASSES: [RegExp, string][] = [
    [/(?<!\\):hover/g, `[${HOVER_ATTRIBUTE}]`],
    [/(?<!\\):focus-within/g, `[${FOCUS_WITHIN_ATTRIBUTE}]`],
    [/(?<!\\):focus-visible/g, `[${FOCUS_ATTRIBUTE}]`],
    [/(?<!\\):focus(?![\w-])/g, `[${FOCUS_ATTRIBUTE}]`],
];

const STYLE_ELEMENT_ATTRIBUTE = 'data-cobrowse-pseudo-class-styles';
const STYLE_RULE = 1;
const MEDIA_RULE = 4;
const SUPPORTS_RULE = 12;

export function buildEmulatedPseudoClassCss(rules: ArrayLike<CSSRule>): string {
    return Array.from(rules)
        .map((rule) => emulateRule(rule))
        .filter((css) => css !== '')
        .join('\n');
}

function emulateRule(rule: CSSRule): string {
    if (rule.type === STYLE_RULE) {
        const { selectorText, style } = rule as CSSStyleRule;
        const emulatedSelector = EMULATED_PSEUDO_CLASSES.reduce((selector, [pseudoClass, attribute]) => selector.replace(pseudoClass, attribute), selectorText);

        return emulatedSelector !== selectorText ? `${emulatedSelector} { ${style.cssText} }` : '';
    }

    if (rule.type !== MEDIA_RULE && rule.type !== SUPPORTS_RULE) {
        return '';
    }

    const groupingRule = rule as CSSMediaRule | CSSSupportsRule;
    const nested = buildEmulatedPseudoClassCss(groupingRule.cssRules);

    return nested === '' ? '' : `@${rule.type === MEDIA_RULE ? 'media' : 'supports'} ${groupingRule.conditionText} { ${nested} }`;
}

// `:hover` cannot be forced from script, and `:focus` stops matching while the client's window is in the background:
// those rules are duplicated onto attributes driven by the agent's pointer and the page's active element.
export class PseudoClassEmulator {
    private hoveredChain: Element[] = [];
    private focusedChain: Element[] = [];
    private readonly processedSheets = new WeakSet<CSSStyleSheet>();
    private styleElement: HTMLStyleElement | null = null;
    private readonly targetDocument: Document;

    constructor(targetDocument: Document = document) {
        this.targetDocument = targetDocument;
        this.targetDocument.addEventListener('focusin', this.scheduleFocusSync, true);
        this.targetDocument.addEventListener('focusout', this.scheduleFocusSync, true);
        this.syncStyles();
        this.syncFocus();
    }

    hover(element: Element | null): void {
        this.syncStyles();

        const nextChain = element ? this.chainOf(element) : [];
        const left = this.hoveredChain.filter((hovered) => !nextChain.includes(hovered));
        const entered = nextChain.filter((hovered) => !this.hoveredChain.includes(hovered));

        if (left.length === 0 && entered.length === 0) {
            return;
        }

        this.dispatchTransition(this.hoveredChain[0] ?? null, element, left, entered);
        left.forEach((hovered) => hovered.removeAttribute(HOVER_ATTRIBUTE));
        entered.forEach((hovered) => hovered.setAttribute(HOVER_ATTRIBUTE, ''));
        this.hoveredChain = nextChain;
    }

    destroy(): void {
        this.targetDocument.removeEventListener('focusin', this.scheduleFocusSync, true);
        this.targetDocument.removeEventListener('focusout', this.scheduleFocusSync, true);
        this.hover(null);
        this.applyFocus([]);
        this.styleElement?.remove();
        this.styleElement = null;
    }

    private readonly scheduleFocusSync = (): void => {
        queueMicrotask(() => this.syncFocus());
    };

    syncFocus(): void {
        const active = this.targetDocument.activeElement;
        const focused = active && active !== this.targetDocument.body ? active : null;

        this.syncStyles();
        this.applyFocus(focused ? this.chainOf(focused) : []);
    }

    private applyFocus(nextChain: Element[]): void {
        this.focusedChain.filter((element) => !nextChain.includes(element)).forEach((element) => element.removeAttribute(FOCUS_WITHIN_ATTRIBUTE));
        this.focusedChain[0]?.removeAttribute(FOCUS_ATTRIBUTE);
        nextChain.forEach((element) => element.setAttribute(FOCUS_WITHIN_ATTRIBUTE, ''));
        nextChain[0]?.setAttribute(FOCUS_ATTRIBUTE, '');
        this.focusedChain = nextChain;
    }

    private syncStyles(): void {
        const newSheets = Array.from(this.targetDocument.styleSheets).filter(
            (sheet) => !this.processedSheets.has(sheet) && !(sheet.ownerNode as Element | null)?.hasAttribute?.(STYLE_ELEMENT_ATTRIBUTE)
        );

        if (newSheets.length === 0) {
            return;
        }

        const css = newSheets.map((sheet) => this.readSheet(sheet)).join('\n');

        if (!this.styleElement) {
            this.styleElement = this.targetDocument.createElement('style');
            this.styleElement.setAttribute(STYLE_ELEMENT_ATTRIBUTE, '');
            this.targetDocument.head.appendChild(this.styleElement);
        }

        this.styleElement.textContent = `${this.styleElement.textContent ?? ''}\n${css}`;
    }

    private readSheet(sheet: CSSStyleSheet): string {
        this.processedSheets.add(sheet);

        try {
            return buildEmulatedPseudoClassCss(sheet.cssRules);
        } catch {
            // Cross-origin stylesheets (FontAwesome kit, Google Fonts) cannot be read: they carry no app interaction rules.
            return '';
        }
    }

    private dispatchTransition(previous: Element | null, next: Element | null, left: Element[], entered: Element[]): void {
        const init = { bubbles: false, cancelable: false, composed: true, view: this.targetDocument.defaultView };

        if (previous) {
            previous.dispatchEvent(new MouseEvent('mouseout', { ...init, bubbles: true, relatedTarget: next }));
        }

        left.forEach((element) => element.dispatchEvent(new MouseEvent('mouseleave', { ...init, relatedTarget: next })));

        if (next) {
            next.dispatchEvent(new MouseEvent('mouseover', { ...init, bubbles: true, relatedTarget: previous }));
        }

        entered.reverse().forEach((element) => element.dispatchEvent(new MouseEvent('mouseenter', { ...init, relatedTarget: previous })));
    }

    private chainOf(element: Element): Element[] {
        const chain: Element[] = [];

        for (let current: Element | null = element; current && current !== this.targetDocument.documentElement; current = current.parentElement) {
            chain.push(current);
        }

        return chain;
    }
}
